import React, { useEffect, useState } from 'react';
import { Check, ChevronDown, Star } from 'lucide-react';
import { facetParam, isLightHex, readMulti, rupees } from '@/lib/shop';

/**
 * Filters driven entirely by the API's `facets`. Each change rewrites the URL
 * (via `onChange(mutator)`), so the listing refetches, the back button undoes
 * a filter and the link can be shared. Counts are disjunctive on the backend:
 * a facet's own counts ignore its selection, so picking "Navy" still shows
 * how many "Black" there are.
 */

const Section = ({ title, children, defaultOpen = true, badge }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-ink-200/70 py-3 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="lk-focus flex w-full items-center justify-between gap-2 rounded py-1 text-left"
      >
        <span className="text-xs font-bold uppercase tracking-wide text-ink-900">
          {title}
          {badge > 0 && <span className="ml-2 rounded-full bg-brand-tint px-1.5 py-0.5 text-[10px] text-brand-secondary">{badge}</span>}
        </span>
        <ChevronDown className={`h-4 w-4 text-ink-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="pt-2">{children}</div>}
    </div>
  );
};

const CheckRow = ({ checked, onClick, label, count, disabled }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    onClick={onClick}
    disabled={disabled}
    className="lk-focus flex min-h-[36px] w-full items-center gap-2.5 rounded-lg px-1 text-left text-sm text-ink-700 transition hover:bg-surface-muted disabled:opacity-40"
  >
    <span
      className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border transition ${
        checked ? 'border-brand-secondary bg-brand-secondary' : 'border-ink-400 bg-white'
      }`}
    >
      {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
    </span>
    <span className="min-w-0 flex-1 truncate">{label}</span>
    {count != null && <span className="tabular text-xs text-ink-400">{count}</span>}
  </button>
);

const RadioRow = ({ checked, onClick, label, count }) => (
  <button
    type="button"
    role="radio"
    aria-checked={checked}
    onClick={onClick}
    className="lk-focus flex min-h-[36px] w-full items-center gap-2.5 rounded-lg px-1 text-left text-sm text-ink-700 transition hover:bg-surface-muted"
  >
    <span className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border ${checked ? 'border-brand-secondary' : 'border-ink-400'}`}>
      {checked && <span className="h-2.5 w-2.5 rounded-full bg-brand-secondary" />}
    </span>
    <span className="min-w-0 flex-1 truncate">{label}</span>
    {count != null && <span className="tabular text-xs text-ink-400">{count}</span>}
  </button>
);

const toggleValue = (params, key, value) => (p) => {
  const values = readMulti(params, key);
  const next = values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
  if (next.length) p.set(facetParam(key), next.join(','));
  else p.delete(facetParam(key));
};

const MultiFacet = ({ facet, params, onChange }) => {
  const [all, setAll] = useState(false);
  const selected = readMulti(params, facet.key);
  const options = facet.options || [];
  const shown = all ? options : options.slice(0, 6);
  return (
    <Section title={facet.label} badge={selected.length}>
      <div className="space-y-0.5">
        {shown.map((o) => (
          <CheckRow
            key={o.value}
            checked={selected.includes(String(o.value))}
            onClick={() => onChange(toggleValue(params, facet.key, String(o.value)))}
            label={o.label}
            count={o.count}
            disabled={!o.count && !selected.includes(String(o.value))}
          />
        ))}
      </div>
      {options.length > 6 && (
        <button type="button" onClick={() => setAll((a) => !a)} className="lk-focus mt-1 rounded px-1 text-xs font-semibold text-brand-secondary">
          {all ? 'Show less' : `+ ${options.length - 6} more`}
        </button>
      )}
    </Section>
  );
};

const ColorFacet = ({ facet, params, onChange }) => {
  const selected = readMulti(params, facet.key);
  return (
    <Section title={facet.label} badge={selected.length}>
      <div className="grid grid-cols-4 gap-x-1 gap-y-3">
        {(facet.options || []).map((o) => {
          const on = selected.includes(String(o.value));
          return (
            <button
              key={o.value}
              type="button"
              role="checkbox"
              aria-checked={on}
              aria-label={`${o.label} (${o.count})`}
              disabled={!o.count && !on}
              onClick={() => onChange(toggleValue(params, facet.key, String(o.value)))}
              className="lk-focus group flex flex-col items-center gap-1 rounded-lg py-1 disabled:opacity-35"
            >
              <span
                className={`relative flex h-8 w-8 items-center justify-center rounded-full transition ${
                  on ? 'ring-2 ring-brand-secondary ring-offset-2' : isLightHex(o.hex) ? 'ring-1 ring-ink-200' : 'group-hover:ring-1 group-hover:ring-ink-400 group-hover:ring-offset-2'
                }`}
                style={{
                  background: o.value === 'multicolor' ? 'conic-gradient(#ef4444,#f59e0b,#22c55e,#3b82f6,#a855f7,#ef4444)' : o.hex || '#ccc',
                }}
              >
                {on && <Check className={`h-4 w-4 ${isLightHex(o.hex) ? 'text-ink-900' : 'text-white'}`} strokeWidth={3} />}
              </span>
              <span className="w-full truncate text-center text-[11px] leading-tight text-ink-700">{o.label}</span>
            </button>
          );
        })}
      </div>
    </Section>
  );
};

const RangeFacet = ({ facet, params, onChange }) => {
  const curMin = params.get('priceMin') || '';
  const curMax = params.get('priceMax') || '';
  const [min, setMin] = useState(curMin);
  const [max, setMax] = useState(curMax);
  useEffect(() => {
    setMin(curMin);
    setMax(curMax);
  }, [curMin, curMax]);

  // A bound of 0 is a real value (the "₹0 – ₹250" bucket); only null/'' mean "none".
  const has = (v) => v != null && v !== '';
  const apply = (lo, hi) =>
    onChange((p) => {
      if (has(lo)) p.set('priceMin', String(lo));
      else p.delete('priceMin');
      if (has(hi)) p.set('priceMax', String(hi));
      else p.delete('priceMax');
    });

  return (
    <Section title={facet.label} badge={curMin || curMax ? 1 : 0}>
      {facet.buckets?.length > 0 && (
        <div className="space-y-0.5" role="radiogroup" aria-label="Price ranges">
          {facet.buckets.map((b) => {
            const from = has(b.from) ? String(b.from) : '';
            const to = has(b.to) ? String(b.to) : '';
            const on = from === curMin && to === curMax;
            return (
              <RadioRow
                key={`${b.from}-${b.to}`}
                checked={on}
                onClick={() => (on ? apply(null, null) : apply(b.from, b.to))}
                label={to ? `${rupees(b.from)} – ${rupees(b.to)}` : `Over ${rupees(b.from)}`}
                count={b.count}
              />
            );
          })}
        </div>
      )}
      <form
        className="mt-3 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const lo = Number(min) || '';
          const hi = Number(max) || '';
          apply(lo && hi && lo > hi ? hi : lo, lo && hi && lo > hi ? lo : hi);
        }}
      >
        <label className="sr-only" htmlFor={`pmin-${facet.key}`}>Minimum price</label>
        <input
          id={`pmin-${facet.key}`}
          inputMode="numeric"
          value={min}
          onChange={(e) => setMin(e.target.value.replace(/\D/g, ''))}
          placeholder={facet.min ? `₹${facet.min}` : 'Min'}
          className="lk-input min-w-0 px-3 py-2 text-sm"
        />
        <span className="text-ink-400">–</span>
        <label className="sr-only" htmlFor={`pmax-${facet.key}`}>Maximum price</label>
        <input
          id={`pmax-${facet.key}`}
          inputMode="numeric"
          value={max}
          onChange={(e) => setMax(e.target.value.replace(/\D/g, ''))}
          placeholder={facet.max ? `₹${facet.max}` : 'Max'}
          className="lk-input min-w-0 px-3 py-2 text-sm"
        />
        <button type="submit" className="lk-btn-ghost min-h-[42px] shrink-0 px-3 py-2">
          Go
        </button>
      </form>
    </Section>
  );
};

const SingleFacet = ({ facet, params, onChange }) => {
  const cur = params.get(facet.key) || '';
  return (
    <Section title={facet.label} badge={cur ? 1 : 0}>
      <div className="space-y-0.5" role="radiogroup" aria-label={facet.label}>
        {(facet.options || []).map((o) => {
          const on = String(o.value) === cur;
          const label =
            facet.key === 'rating' ? (
              <span className="inline-flex items-center gap-1">
                {String(o.value)}
                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> &amp; above
              </span>
            ) : (
              o.label
            );
          return (
            <RadioRow
              key={o.value}
              checked={on}
              label={label}
              count={o.count}
              onClick={() => onChange((p) => (on ? p.delete(facet.key) : p.set(facet.key, String(o.value))))}
            />
          );
        })}
      </div>
    </Section>
  );
};

const FilterPanel = ({ facets = [], params, onChange }) => (
  <div>
    {facets.map((f) => {
      if (f.type === 'range') {
        if (!f.max && !f.buckets?.length && !params.get('priceMin') && !params.get('priceMax')) return null;
        return <RangeFacet key={f.key} facet={f} params={params} onChange={onChange} />;
      }
      if (!f.options?.length) return null;
      if (f.type === 'color') return <ColorFacet key={f.key} facet={f} params={params} onChange={onChange} />;
      if (f.type === 'single') return <SingleFacet key={f.key} facet={f} params={params} onChange={onChange} />;
      return <MultiFacet key={f.key} facet={f} params={params} onChange={onChange} />;
    })}
  </div>
);

export default FilterPanel;
